import { useEffect, useMemo, useRef, useState } from "react"

import { Dropdown } from "antd"
import cls from "classnames"

import InputComponent from "../../../../../Components/Input"
import Icon from "../../../../../Icon"
import { getRiskLevel } from "../../../components/Monitoring/riskLevel"
import { rankDescriptors } from "./descriptorRanking"

import styles from "./DescriptorSelect.module.less"

const formatPercent = (value) => `${Number(value).toFixed(2)}%`

const HighlightedName = ({ name, query }) => {
  const start = query ? name.toLowerCase().indexOf(query) : -1
  if (start === -1) return name

  const end = start + query.length
  return (
    <>
      {name.slice(0, start)}
      <mark>{name.slice(start, end)}</mark>
      {name.slice(end)}
    </>
  )
}

const DescriptorSelect = ({ value, onChange, descriptors, limit }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [activeIndex, setActiveIndex] = useState(-1)
  const triggerRef = useRef(null)
  const listRef = useRef(null)

  const query = search.trim().toLowerCase()
  const vampLimit = Number(limit) > 0 ? Number(limit) : 0

  const { attention, rest } = useMemo(
    () => rankDescriptors(descriptors || [], vampLimit, query),
    [descriptors, vampLimit, query]
  )
  const options = useMemo(() => attention.concat(rest), [attention, rest])

  useEffect(() => {
    listRef.current
      ?.querySelector(`.${styles.option}.${styles.active}`)
      ?.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setSearch("")
      setActiveIndex(-1)
    }
    setOpen(nextOpen)
  }

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const pick = (index) => {
    const option = options[index]
    if (!option) return
    if (option.name !== value) onChange(option.name)
    close()
  }

  const handleSearch = (e) => {
    setSearch(e.target.value)
    setActiveIndex(e.target.value.trim() ? 0 : -1)
  }

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(options.length - 1, i + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      pick(activeIndex)
    } else if (e.key === "Escape") {
      e.stopPropagation()
      close()
    }
  }

  const renderOption = (option, index, isAttention) => (
    <div
      key={option.name}
      role="option"
      aria-selected={option.name === value}
      className={cls(styles.option, {
        [styles.attention]: isAttention,
        [styles.selected]: option.name === value,
        [styles.active]: index === activeIndex
      })}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={() => pick(index)}
    >
      <span className={styles.optionName}>
        <HighlightedName name={option.name} query={query} />
      </span>
      <span className={cls(styles.ratio, styles[getRiskLevel(option.ratio, vampLimit)])}>
        {formatPercent(option.ratio)}
      </span>
      <Icon type="circle_check" className={styles.check} />
    </div>
  )

  const restTitle = query
    ? `Results · ${rest.length}`
    : attention.length
      ? "Other descriptors"
      : "All descriptors"

  const popup = (
    <div className={styles.popup}>
      <div className={styles.searchWrapper}>
        <InputComponent
          autoFocus
          className={styles.search}
          prefix={<Icon type="search" />}
          placeholder="Search descriptor..."
          aria-label="Search descriptor"
          autoComplete="off"
          value={search}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className={styles.list} role="listbox" ref={listRef}>
        {!options.length && (
          <div className={styles.empty}>
            <Icon type="empty_warning" className={styles.emptyIcon} />
            No results found
          </div>
        )}
        {attention.length > 0 && (
          <>
            <div className={styles.group}>
              <span className={styles.groupTitleStrong}>Needs attention</span>
              <span>VAMP limit {formatPercent(vampLimit)}</span>
            </div>
            {attention.map((option, index) => renderOption(option, index, true))}
            <div className={styles.separator} />
          </>
        )}
        {rest.length > 0 && (
          <>
            <div className={styles.group}>
              <span>{restTitle}</span>
              <span>VAMP ratio</span>
            </div>
            {rest.map((option, index) => renderOption(option, attention.length + index, false))}
          </>
        )}
      </div>
    </div>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      trigger={["click"]}
      placement="bottomRight"
      destroyPopupOnHide
      getPopupContainer={() => document.getElementById("main-layout")}
      dropdownRender={() => popup}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Billing descriptor"
        className={cls(styles.trigger, { [styles.open]: open })}
      >
        <Icon type="search" className={styles.triggerIcon} />
        <span className={styles.triggerValue}>{value}</span>
        <Icon type="chevron_down_small" className={styles.chevron} />
      </button>
    </Dropdown>
  )
}

export default DescriptorSelect
